/**
 * Medallion — reusable cSquare-branded circular seal for any framework or
 * regulation. Teal field on top with the framework name, a mountain
 * silhouette acting as the visual separator into a cream cartouche carrying
 * the cSquare GRC wordmark.
 */

export type MedallionAccent =
  | "blue" | "red" | "green" | "purple" | "orange"
  | "slate" | "navy" | "amber" | "teal" | "violet" | "brand";

export type MedallionVariant = "card" | "showcase";
export type MedallionTone = "dark" | "light";

interface MedallionProps {
  name: string;
  subLabel?: string;
  accent?: MedallionAccent;
  size?: number;
  variant?: MedallionVariant;
  tone?: MedallionTone;
  className?: string;
}

const ACCENTS: Record<MedallionAccent, string> = {
  blue:   "#3B82F6",
  red:    "#F87171",
  green:  "#34D399",
  purple: "#A78BFA",
  orange: "#FB923C",
  slate:  "#94A3B8",
  navy:   "#60A5FA",
  amber:  "#FBBF24",
  teal:   "#2DD4BF",
  violet: "#C4B5FD",
  brand:  "#A78BFA",
};

const INK   = "#1A1A2E";
const CREAM = "#FAFAF7";
const TEAL_FIELD = "#0F4F4A"; // deep teal for top field on dark tone
const TEAL_FIELD_LIGHT = "#E6F4F2"; // soft teal for light tone

function layoutName(name: string, hasSub: boolean): { lines: string[]; fontSize: number } {
  const len = name.length;
  if (len <= 4)  return { lines: [name], fontSize: hasSub ? 30 : 36 };
  if (len <= 7)  return { lines: [name], fontSize: hasSub ? 24 : 30 };
  if (len <= 10) return { lines: [name], fontSize: 21 };
  const idx = name.lastIndexOf(" ");
  if (idx > 0) {
    return { lines: [name.slice(0, idx), name.slice(idx + 1)], fontSize: 18 };
  }
  return { lines: [name], fontSize: 16 };
}

export function Medallion({
  name,
  subLabel,
  accent = "brand",
  size = 96,
  variant = "card",
  tone = "dark",
  className,
}: MedallionProps) {
  const accentColor = ACCENTS[accent] ?? ACCENTS.brand;
  const isDark = tone === "dark";
  const field = isDark ? TEAL_FIELD : TEAL_FIELD_LIGHT;
  const nameFill = isDark ? "#FFFFFF" : INK;
  const ringStroke = isDark ? "rgba(255,255,255,0.18)" : "rgba(26,26,46,0.35)";

  const hasSub = Boolean(subLabel);
  const { lines, fontSize } = layoutName(name, hasSub);
  const lineHeight = fontSize * 1.0;

  // Center name block in the upper teal portion (above cartouche y=140..200).
  const blockH = lines.length * lineHeight;
  const centerY = 64;
  const startY = centerY - blockH / 2 + lineHeight * 0.78;

  // Unique clipPath id per render so multiple medallions don't collide.
  const clipId = `med-clip-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 200"
      width={size}
      height={size}
      role="img"
      aria-label={`${name}${subLabel ? ` ${subLabel}` : ""} readiness medallion — Powered by cSquare GRC`}
      className={className}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx={100} cy={100} r={94} />
        </clipPath>
      </defs>

      {/* All inner content clipped to the disc */}
      <g clipPath={`url(#${clipId})`}>
        {/* field */}
        <rect x={0} y={0} width={200} height={200} fill={field} />

        {/* framework name */}
        <g
          fontFamily="'IBM Plex Sans', system-ui, sans-serif"
          fontWeight={700}
          fontSize={fontSize}
          fill={nameFill}
          textAnchor="middle"
        >
          {lines.map((ln, i) => (
            <text key={i} x={100} y={startY + i * lineHeight}>
              {ln}
            </text>
          ))}
          {hasSub && (
            <text
              x={100}
              y={startY + lines.length * lineHeight + 2}
              fontSize={Math.max(10, fontSize * 0.38)}
              fontWeight={600}
              fill={nameFill}
              opacity={0.85}
              style={{ letterSpacing: "2px" }}
            >
              {subLabel?.toUpperCase()}
            </text>
          )}
        </g>

        {/* bottom cartouche — clean cream band, clipped to the disc */}
        <rect x={0} y={132} width={200} height={68} fill={CREAM} />

        {/* mountain silhouette as separator — brand ink primary ridge,
            with a subtle accent-colored back ridge for hue cue */}
        <polygon
          points="0,134 38,130 64,116 84,126 104,100 126,120 150,110 172,126 200,130 200,140 0,140"
          fill={INK}
          opacity={1}
        />
        {/* secondary ridge for depth — framework accent at low opacity */}
        <polygon
          points="0,136 30,132 58,124 80,132 100,116 122,130 148,120 176,132 200,134 200,142 0,142"
          fill={accentColor}
          opacity={0.35}
        />

        {/* cartouche text */}
        <text
          x={100} y={164}
          fontFamily="'IBM Plex Sans', system-ui, sans-serif"
          fontSize={9}
          fill={TEAL_FIELD}
          textAnchor="middle"
        >
          Powered by
        </text>
        <text
          x={100} y={180}
          fontFamily="'IBM Plex Sans', system-ui, sans-serif"
          fontWeight={700}
          fontSize={12}
          fill={INK}
          textAnchor="middle"
          style={{ letterSpacing: "0.2px" }}
        >
          cSquare GRC
        </text>
      </g>

      {/* outer hairline ring (drawn last so it sits on top) */}
      <circle cx={100} cy={100} r={94} fill="none" stroke={ringStroke} strokeWidth={1.25} />
    </svg>
  );
}

export default Medallion;
